'use client'

import ActivityLoading from '@/components/activity-loading'

import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { SnipeShield } from '@/components/secure-listing-badge'
import useRecentListings from '@/utils/hooks/useRecentListings'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import { formatActivityBtc } from '@/utils/format-activity-btc'
import { formatActivityUsd } from '@/utils/format-activity-usd'
import BitcoinIcon from '@/icons/bitcoin-filled'
import InscriptionContent from '@/components/inscription-content'
import CollectionIcon from '@/components/collection-icon'
import VerifiedBadge from '@/components/verified-badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import isFungible from '@/utils/is-fungible'
import isRune from '@/utils/is-rune'
import isAlkanes from '@/utils/is-alkanes'
import numberWithCommas from '@/utils/format-number'
import getRelativeTime from '@/utils/get-relative-time'
import useInscriptionModal from '@/utils/hooks/useInscriptionModal'
import { listedAtMs } from './listed-at'

type Listing = any

const itemName = (item: Listing) =>
	item.meta?.name ||
	item.collection?.name
		?.replace('TAP ', '')
		.replace('BRC20 ', '')
		.replace('DRC20 ', '')
		.replace('BEL20 ', '') ||
	(item.num != null ? `Inscription #${item.num}` : item.id?.slice(0, 8)) ||
	'Unknown'

/**
 * Newest listings added to Ordinals Wallet, in the same row style as the
 * mempool feed: when it was listed, what it is, and the asking price.
 */
const ListingsFeed: React.FC<{ hideHeader?: boolean; onSwitchTab?: () => void }> = ({
	hideHeader = false,
	onSwitchTab
}): ReactElement => {
	const router = useRouter()
	const openInscriptionModal = useInscriptionModal()
	const { exchangeRate } = useExchangeRate()
	const { data, error, isLoading } = useRecentListings(50)

	const items: Listing[] = useMemo(() => {
		if (!Array.isArray(data)) return []
		return [...(data as Listing[])].sort(
			(a, b) => listedAtMs(b.listed_at) - listedAtMs(a.listed_at)
		)
	}, [data])

	// Keep the relative timestamps moving: every 2s while a row is under a
	// minute old, otherwise every 30s so "18m" still advances between refreshes.
	const [, setTick] = useState(0)
	useEffect(() => {
		const hasRecent = items.some((item) => Date.now() - listedAtMs(item.listed_at) < 60_000)
		const interval = setInterval(() => setTick((t) => t + 1), hasRecent ? 2000 : 30_000)
		return () => clearInterval(interval)
	}, [items])

	const renderItemContent = (item: Listing) => {
		const isItemFungible = isFungible(item?.collection?.slug) || !!item?.rune_id
		const jsonAmount = item?.amount || item?.meta?.attributes?.[0]?.value
		const isRuneItem = !!item?.rune_id || isRune(item?.collection?.slug)
		const isImage = item?.content_type?.includes('image') || item?.collection?.slug === 'bitmap'
		const isText =
			!isImage && (item?.content_type?.includes('text') || item?.content_type?.includes('json'))

		return (
			<TooltipProvider delayDuration={0}>
				<Tooltip>
					<TooltipTrigger asChild>
						<div className="flex min-w-0 items-center gap-x-1 w-full">
							<div className="flex items-center min-w-[1rem] min-h-[1rem] size-4">
								{isRuneItem || isItemFungible || (item?.collection?.slug && !isImage) ? (
									<CollectionIcon
										collection={item.collection}
										className="inline mr-1 size-4 min-w-[1rem] min-h-[1rem] rounded-sm"
										imageClassName="size-4 min-w-[1rem] min-h-[1rem] rounded-sm"
									/>
								) : isText ? (
                                    <div aria-hidden="true" className="mr-1 size-4 min-w-4 min-h-4 rounded-sm bg-gradient-to-br from-purple-600 via-violet-500 to-blue-500" />
								) : (
									<InscriptionContent
										className="inline mr-1 size-4 min-h-[1rem] min-w-[1rem] rounded-sm"
										inscription={item}
										isDetail={false}
										showPricePer={true}
										smallCard={true}
									/>
								)}
							</div>
							<span className="min-w-0 text-xs truncate">
								{isItemFungible && jsonAmount && `${numberWithCommas(jsonAmount)} `}
								{itemName(item)}
								<VerifiedBadge slug={item?.collection?.slug} />
							</span>
						</div>
					</TooltipTrigger>
					<TooltipContent
						side="left"
						className="flex flex-col p-3 bg-popover border border-border rounded-md shadow-md"
					>
						<div className="text-xs text-muted-foreground mb-2">
							{item?.meta?.name ||
								item.collection?.name ||
								(item.num ? `Inscription #${item.num}` : 'Unknown')}
						</div>
						<div className="size-[160px] sm:size-[256px] bg-card rounded-sm overflow-hidden">
							{isRuneItem || isAlkanes(item?.collection?.slug) ? (
								<CollectionIcon
									collection={item.collection}
									className="inline mr-1 size-4 min-w-[1rem] min-h-[1rem] rounded-sm"
									imageClassName="h-full w-full min-w-[1rem] min-h-[1rem] rounded-sm"
								/>
							) : (
								<InscriptionContent
									className="size-full"
									inscription={item}
									isDetail={false}
									showPricePer={true}
									smallCard={true}
								/>
							)}
						</div>
						<div className="flex items-center justify-between mt-2 text-xs">
							<span className="text-muted-foreground">Listed</span>
							{item.escrow && (
								<span className="font-medium flex items-center">
									<BitcoinIcon className="inline mr-1 size-3" />
									{formatActivityBtc(item.escrow.satoshi_price)}
								</span>
							)}
						</div>
						<div className="flex items-center justify-between mt-1 text-xs">
							<div className="text-muted-foreground">
								{item.escrow?.seller_address && (
									<Link
										href={`/address/${item.escrow.seller_address}`}
										className="hover:text-primary"
										onClick={(e) => e.stopPropagation()}
									>
										{item.escrow.seller_address.slice(-5)}
									</Link>
								)}
							</div>
							{item.escrow && exchangeRate > 0 && (
								<span className="text-muted-foreground">
									${formatActivityUsd((item.escrow.satoshi_price / 1e8) * exchangeRate)}
								</span>
							)}
						</div>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		)
	}

	// A listing is something you can buy: open the item itself (same as the
	// sales feed), and fall back to the collection for rune/fungible rows.
	const handleRowClick = (item: Listing) => {
		if (item.id) {
			openInscriptionModal({ id: item.id, inscription: item })
		} else if (item.collection?.slug) {
			router.push(`/collection/${item.collection.slug}`)
		}
	}

	// Rune batches can share ticker, seller and timestamp; the feed has no
	// individual rune listing ID, so retain the row offset to distinguish them.
	const rowKey = (item: Listing, i: number) =>
		`${item.id || `${item.rune_id || 'x'}:${i}`}-${item.escrow?.seller_address || ''}-${
			item.listed_at || i
		}`

	return (
		<div className="flex flex-col h-full bg-transparent">
			{!hideHeader && (
				<div className="z-[1] px-4 py-3 border-b border-border">
					<h1 className="text-xs font-medium text-foreground -ml-1">
						Recent Listings
						{items.length > 0 && (
							<span className="text-muted-foreground ml-1 font-normal">{items.length}</span>
						)}
					</h1>
				</div>
			)}
			<div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
				{isLoading && (
					<ActivityLoading />
				)}
				{error && !items.length && (
					<div className="flex justify-center items-center py-8 text-sm text-muted-foreground">
						Unable to load recent listings
					</div>
				)}
				{!isLoading && !error && items.length === 0 && (
					<div className="flex flex-col justify-center items-center py-8 gap-3 text-sm text-muted-foreground">
						No new listings yet
						{onSwitchTab && (
							<button
								onClick={onSwitchTab}
								className="px-3 py-1.5 text-xs border border-border rounded text-muted-foreground hover:bg-white/5 transition-colors"
							>
								Switch Tab
							</button>
						)}
					</div>
				)}
				{items.length > 0 && (
					<Table className="w-full table-auto">
						<TableBody>
							{items.map((item, i) => (
								<TableRow
									key={rowKey(item, i)}
									className="whitespace-nowrap border-0 hover:bg-accent/50 cursor-pointer transition-all my-px"
									onClick={() => handleRowClick(item)}
								>
									<TableCell className="w-[44px] py-0 pl-3 pr-1 text-xs text-muted-foreground text-left">
										{item.id ? (
											<Link
												href={`/inscription/${item.id}`}
												onClick={(e) => e.stopPropagation()}
												className="hover:text-primary flex items-center gap-0.5 py-1"
											>
												{getRelativeTime(listedAtMs(item.listed_at))}
											</Link>
										) : (
											<span className="flex items-center py-1">
												{getRelativeTime(listedAtMs(item.listed_at))}
											</span>
										)}
									</TableCell>
									<TableCell className="w-full max-w-0 p-1 text-xs text-left overflow-hidden">
										{renderItemContent(item)}
									</TableCell>
									<TableCell className="py-1 pr-3 sm:pr-4 pl-1 text-xs text-right">
										<div className="flex items-center justify-end gap-0.5 whitespace-nowrap">
											{(item.escrow?.protected === true || item.escrow?.private_relay === true) && (
												<SnipeShield
													className="mr-0.5 inline-flex shrink-0 items-center text-emerald-400"
													iconClassName="size-3"
												/>
											)}
											{/* Every row here is an Ordinals Wallet listing; say so like the sales feed does. */}
											<span
												title="Listed on Ordinals Wallet"
												aria-label="Listed on Ordinals Wallet"
												className="mr-1 inline-flex shrink-0 items-center justify-center size-[14px] rounded-[3px] overflow-hidden"
											>
												<img src="/logo-icon.svg" alt="OW" className="w-full h-full" />
											</span>
											<BitcoinIcon className="shrink-0 size-3 relative -top-[0.5px]" />
											<span className="tabular-nums whitespace-nowrap">
												{formatActivityBtc(item.escrow?.satoshi_price || 0)}
												{exchangeRate > 0 && (
													<span className="text-muted-foreground ml-0.5 text-[10px]">
														($
														{formatActivityUsd(((item.escrow?.satoshi_price || 0) / 1e8) * exchangeRate)}
														)
													</span>
												)}
											</span>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</div>
		</div>
	)
}

export default ListingsFeed
